import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import {
  createShipMethod,
  deleteShipMethod,
  updateShipMethod,
  type ShipMethodDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Null to create. */
  method: ShipMethodDto | null;
  onSaved: () => void;
}

interface FormState {
  shipMethodCode: string;
  displayName: string;
  carrier: string;
  isActive: boolean;
}

/**
 * Creates or edits one carrier.
 *
 * Delete is offered only for a carrier nothing names. Once an order, pick or shipment does,
 * deactivating is the answer: the carrier leaves the picker and everything already on it keeps
 * reading correctly. The current product has no delete at all, so this rule is the payment-term
 * master's rather than a copied one.
 */
export function ShipMethodFormModal({ opened, onClose, method, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = method !== null;

  const [form, setForm] = useState<FormState>({
    shipMethodCode: '',
    displayName: '',
    carrier: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setForm({
      shipMethodCode: method?.shipMethodCode ?? '',
      displayName: method?.displayName ?? '',
      carrier: method?.carrier ?? '',
      isActive: method?.isActive ?? true,
    });
  }, [opened, method]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        shipMethodCode: form.shipMethodCode.trim().toUpperCase(),
        displayName: form.displayName.trim(),
        // Empty means the carrier has no grouping name, which is the common case: the current
        // product has no such field at all, only a 15-character description.
        carrier: form.carrier.trim() === '' ? null : form.carrier.trim(),
        isActive: form.isActive,
      };
      return isEdit ? updateShipMethod(method!.shipMethodId, body) : createShipMethod(body);
    },
    onSuccess: () => {
      notifications.show({ message: t('shipMethods.saved'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  const remove = useMutation({
    mutationFn: () => deleteShipMethod(method!.shipMethodId),
    onSuccess: () => {
      notifications.show({ message: t('shipMethods.deleted'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  /** The server's own sentence when it has one — each refusal says what to do instead. */
  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      return err.problem.detail ?? err.problem.title ?? t('shipMethods.errors.saveFailed');
    }
    return t('shipMethods.errors.saveFailed');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const next: typeof errors = {};
    if (!form.shipMethodCode.trim()) next.shipMethodCode = t('shipMethods.errors.codeRequired');
    if (!form.displayName.trim()) next.displayName = t('shipMethods.errors.descriptionRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate();
  }

  const canDelete = isEdit && !method!.isInUse;

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('shipMethods.editTitle', { code: method!.shipMethodCode }) : t('shipMethods.newTitle')}
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('shipMethods.code')}
              value={form.shipMethodCode}
              onChange={(e) => setForm({ ...form, shipMethodCode: e.currentTarget.value.toUpperCase() })}
              error={errors.shipMethodCode}
              maxLength={50}
              required
              autoFocus={!isEdit}
            />
            <TextInput
              label={t('shipMethods.description')}
              description={t('shipMethods.descriptionHint')}
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.currentTarget.value })}
              error={errors.displayName}
              maxLength={200}
              required
              autoFocus={isEdit}
            />
          </SimpleGrid>

          <TextInput
            label={t('shipMethods.carrier')}
            description={t('shipMethods.carrierHint')}
            value={form.carrier}
            onChange={(e) => setForm({ ...form, carrier: e.currentTarget.value })}
            maxLength={200}
            w={{ base: '100%', sm: 320 }}
          />

          <Switch
            label={t('shipMethods.active')}
            description={t('shipMethods.activeHint')}
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
          />

          {isEdit && method!.isInUse && (
            <Text size="xs" c="dimmed">
              {t('shipMethods.inUseHint')}
            </Text>
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="space-between">
            {canDelete ? (
              <Button variant="light" color="red" onClick={() => remove.mutate()} loading={remove.isPending}>
                {t('shipMethods.delete')}
              </Button>
            ) : (
              <span />
            )}
            <Group>
              <Button variant="default" onClick={onClose}>
                {t('shell.cancel')}
              </Button>
              <Button type="submit" color="invetrak" loading={save.isPending}>
                {t('shell.saveChanges')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
